// src/oura/merchant/drawers/RequestDrawer.tsx
//
// One of the merchant's own requests, opened from a row of "Your requests"
// on Work (specs v1.1, A-K): a change they sent with Ask for a change, or a
// call they asked for. Read only. The status line says where it stands; the
// body holds our reply and their words in full, with their files.
//
// A change can be followed up: "Ask a follow-up" closes this and opens Ask
// for a change about it, so the follow-up lands in Your requests as well,
// never in an email thread only one person can see. A call has nothing to
// follow up here: its time comes by email.

import { PaperclipIcon, PhoneIcon, MessageSquarePlusIcon } from "lucide-react";
import type { MyCallRequest, MyChangeRequest } from "@service/aeo/clientReview";
import { AREA_TEXT, DrawerBlock, DrawerFoot, DrawerHead, DrawerQuote, Drawer, FOCUS_RING, PillButton, Tag, TYPE, type DrawerReturnFocus, type DrawerStatus } from "@oura/ui";
import { docDate } from "@oura/docs";
import { cn } from "../../lib/cn";
import { MY_REQUEST_GROUP, OURA_INBOX, otherSender, smallChangeTag, type MyRequestRow } from "../myRequests";
import type { ChangeAbout } from "./ChangeRequestDrawer";
import { ReplyPictureGrid } from "../../components/ReplyPictures";

export function RequestDrawer({
    row,
    extra = false,
    viewer,
    manager,
    email,
    canFollowUp,
    onFollowUp,
    onClose,
    returnFocus,
}: {
    /** The request on show; null keeps the drawer shut. */
    row: MyRequestRow | null;
    /** A second small change filed in one month: named as an extra, as its row names it. */
    extra?: boolean;
    /** The signed-in merchant: their own request needs no "by". */
    viewer: string;
    /** The account manager from the payload, or null when nobody is assigned. */
    manager: string | null;
    /** The address the call's time comes from (`contactEmail`), or null. */
    email: string | null;
    /** Ask for a change can send, and this is not a staffer's preview. */
    canFollowUp: boolean;
    onFollowUp: (about: ChangeAbout) => void;
    onClose: () => void;
    returnFocus?: DrawerReturnFocus;
}) {
    if (!row) return null;
    return (
        <Drawer open onClose={onClose} returnFocus={returnFocus}>
            {row.kind === "change" ? (
                <ChangeSheet request={row.change} extra={extra} viewer={viewer} manager={manager} email={email} canFollowUp={canFollowUp} onFollowUp={onFollowUp} />
            ) : (
                <CallSheet call={row.call} viewer={viewer} manager={manager} email={email} />
            )}
        </Drawer>
    );
}

/**
 * A request's body, without a drawer around it (#4062 MR-FE-7).
 *
 * The drawer covered the page to show a reply and a button. On Our work the
 * row opens where it is and shows the same thing; the drawer keeps serving
 * the switch-off path and the deep link.
 */
export function RequestSheet(props: {
    row: MyRequestRow;
    extra?: boolean;
    viewer: string;
    manager: string | null;
    email: string | null;
    canFollowUp: boolean;
    onFollowUp: (about: ChangeAbout) => void;
}) {
    const { row, extra = false, viewer, manager, email, canFollowUp, onFollowUp } = props;
    return row.kind === "change" ? (
        <ChangeSheet request={row.change} extra={extra} viewer={viewer} manager={manager} email={email} canFollowUp={canFollowUp} onFollowUp={onFollowUp} />
    ) : (
        <CallSheet call={row.call} viewer={viewer} manager={manager} email={email} />
    );
}

export function ChangeSheet({
    request: r,
    extra,
    viewer,
    manager,
    email,
    canFollowUp,
    onFollowUp,
}: {
    request: MyChangeRequest;
    extra: boolean;
    viewer: string;
    manager: string | null;
    email: string | null;
    canFollowUp: boolean;
    onFollowUp: (about: ChangeAbout) => void;
}) {
    const done = r.status === "done";
    const group = MY_REQUEST_GROUP[done ? "done" : "with_us"];
    const sent = docDate(r.at);
    const replied = docDate(r.reply?.at);
    const clock = done
        ? docDate(r.doneAt) ?? null
        : r.reply
            ? `${r.reply.by || "We"} replied${replied ? ` ${replied}` : ""}`
            : sent && `Sent ${sent}`;
    const status: DrawerStatus = { tone: group.tone, label: group.label, clock: clock ? { text: clock } : null };
    // The row's own words for it: an extra small change is never "This month's".
    const tag = smallChangeTag(r, extra);
    const other = otherSender(r.requestedBy, viewer);
    const files = r.files.length;
    return (
        <>
            {/* Her own request: the people purple, not the red of what is late. The files are listed in the body, so no paperclip count here. */}
            <DrawerHead
                lead={<MessageSquarePlusIcon aria-hidden size={14} strokeWidth={2} className={AREA_TEXT.people} />}
                title="Your request"
                status={status}
                chips={tag ? <Tag tone={tag.tone}>{tag.label}</Tag> : undefined}
            />
            {/* A date the status line already says is not said again under it. */}
            {r.reply && (
                <DrawerBlock label={`${r.reply.by || "We"} replied`}>
                    <DrawerQuote who="" at={done ? r.reply.at : null}>
                        {r.reply.body}
                    </DrawerQuote>
                    <ReplyPictureGrid files={r.reply.files} />
                </DrawerBlock>
            )}
            <DrawerBlock label={other ? `${other} asked` : "You asked"}>
                <DrawerQuote who="" at={done || r.reply ? r.at : null}>
                    {r.note}
                </DrawerQuote>
                {files > 0 && (
                    <ul aria-label="Files sent" className="list-plain m-0 mt-2 flex flex-wrap gap-2 p-0">
                        {r.files.map((f, i) => (
                            <li key={`${f.name}-${i}`} className="min-w-0 max-w-full">
                                {f.url ? (
                                    <a
                                        href={f.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className={cn(TYPE.tc, "inline-flex min-h-8 max-w-full items-center gap-1.5 rounded-full bg-gray-50 px-3 font-medium text-blue-600 hover:bg-gray-100", FOCUS_RING)}
                                    >
                                        <PaperclipIcon aria-hidden size={14} className="shrink-0" />
                                        <span className="truncate">{f.name}</span>
                                    </a>
                                ) : (
                                    <span className={cn(TYPE.tc, "inline-flex min-h-8 max-w-full items-center gap-1.5 rounded-full bg-gray-50 px-3 text-gray-700")}>
                                        <PaperclipIcon aria-hidden size={14} className="shrink-0 text-gray-400" />
                                        <span className="truncate">{f.name}</span>
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </DrawerBlock>
            {/* With us and not answered yet: what happens next. */}
            {!done && !r.reply && (
                <p className={cn(TYPE.tc, "m-0 px-1 text-gray-500")}>
                    {manager ?? "Your account manager"} replies by email from {email ?? OURA_INBOX}.
                </p>
            )}
            {canFollowUp && <DrawerFoot second={<PillButton onClick={() => onFollowUp({ id: r.id, at: r.at, note: r.note })}>Ask a follow-up</PillButton>} />}
        </>
    );
}

export function CallSheet({ call: c, viewer, manager, email }: { call: MyCallRequest; viewer: string; manager: string | null; email: string | null }) {
    const closed = c.status === "closed";
    const group = MY_REQUEST_GROUP[closed ? "done" : "with_us"];
    const sent = docDate(c.at);
    const clock = c.status === "scheduled" ? "Call booked" : sent ? `Sent ${sent}` : null;
    const other = otherSender(c.requestedBy, viewer);
    const note = c.note.trim();
    return (
        <>
            <DrawerHead
                lead={<PhoneIcon aria-hidden size={14} strokeWidth={2} className={AREA_TEXT.people} />}
                title="Your call request"
                status={{ tone: group.tone, label: group.label, clock: clock ? { text: clock } : null }}
            />
            <DrawerBlock label={other ? `${other} asked` : "You asked"}>
                {c.about && <p className="m-0">About &ldquo;{c.about}&rdquo;</p>}
                {c.preference.trim() && <p className={cn("m-0 text-gray-600", c.about && "mt-1")}>{c.preference.trim()}</p>}
                {note && (
                    <div className="mt-2">
                        <DrawerQuote who="" at={c.status === "scheduled" ? c.at : null}>
                            {note}
                        </DrawerQuote>
                    </div>
                )}
            </DrawerBlock>
            {c.status === "scheduled" && (
                <DrawerBlock>
                    <p className="m-0">
                        {manager ?? "Your account manager"} will call you. The time is in your email from {email ?? OURA_INBOX}.
                    </p>
                </DrawerBlock>
            )}
        </>
    );
}
